import DataObjectMap from 'assets/components/dataObjectMap/DataObjectMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { defaultInputDateFormat } from 'assets/utils/data/Date';
import { useEffect } from 'react';
import { useSalesContractProvider } from '../../../Sales.Contract.Hooks';
import { ContractRefundCancellationForm } from '../Contract.RefundCancellation';

const ContractRefundCancellationRefund = () => {
  const { form } = useModalForm<ContractRefundCancellationForm>();
  const { lang } = getLocales();
  const { selected } = useSalesContractProvider();
  const { isDealer } = useAuthStore();

  useEffect(() => {
    form.merge({
      refundPricingDistributions: selected?.pricingDistributions || {},
      refundDistributorDistributions: selected?.distributorDistributions || {},
      refundSubfees: selected?.subfees || [],
      refundEnhancements:
        selected?.contractEnhancements?.map((it) => ({
          enhancementId: it.id,
          name: it.productEnhancement?.name,
          pricingDistributions: it.pricingDistributions || {},
          distributorDistributions: it.distributorDistributions || {},
        })) || [],
    });
  }, []);

  return (
    <>
      <FormWrapper>
        <DateInput
          name="refundDateAtLocal"
          label={lang.refundDate}
          dateTimeFormat={defaultInputDateFormat}
          isLocal={true}
          value={form?.data?.refundDateAtLocal}
          error={form?.errors?.refundDateAtLocal}
          onChange={form.update}
          icon={<Icon class="fas-calendar-alt" />}
        />
        &nbsp;
      </FormWrapper>
      <h3 style={{ paddingLeft: '35px' }}>{lang.productsDistributions}</h3>
      <table className={ThemeTableSimple}>
        <thead>
          <tr>
            <th>{lang.pricingDistributions}</th>
            <th>{lang.originalPrice}</th>
            <th>{lang.adjustedPrice}</th>
          </tr>
        </thead>
        <tbody>
          <DataObjectMap
            data={form.data.refundPricingDistributions}
            render={({ data: pricingInfo, key }) => {
              const old = selected?.pricingDistributions[key];
              return (
                <tr key={key}>
                  <td>{key}</td>
                  <td>
                    <FormatValue value={`${(old?.amount ?? 0).toFixed(2)}$`} />
                  </td>
                  <td>
                    <NumberInput
                      name="amount"
                      value={pricingInfo.amount}
                      onChange={({ amount }) =>
                        form.merge({
                          refundPricingDistributions: {
                            [key]: { amount },
                          },
                        })
                      }
                      style={{ formInput: { minWidth: '157px' }, input: { textAlign: 'right' } }}
                      icon={<Icon class="fas-dollar-sign" />}
                      min={0}
                      step={0.01}
                      roundOnBlur
                    />
                  </td>
                </tr>
              );
            }}
          />
        </tbody>

        {!isDealer && Object.keys(selected?.distributorDistributions).length > 0 && (
          <>
            <thead>
              <tr>
                <th>{lang.distributorDistributions}</th>
                <th>{lang.originalPrice}</th>
                <th>{lang.adjustedPrice}</th>
              </tr>
            </thead>
            <tbody>
              <DataObjectMap
                data={form.data.refundDistributorDistributions}
                render={({ data: pricingInfo, key }) => {
                  const old = selected?.distributorDistributions[key];
                  return (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>
                        <FormatValue value={`${(old?.amount ?? 0).toFixed(2)}$`} />
                      </td>
                      <td>
                        <NumberInput
                          name="amount"
                          value={pricingInfo.amount}
                          onChange={({ amount }) =>
                            form.merge({
                              refundDistributorDistributions: {
                                [key]: { amount },
                              },
                            })
                          }
                          style={{ formInput: { minWidth: '157px' }, input: { textAlign: 'right' } }}
                          icon={<Icon class="fas-dollar-sign" />}
                          min={0}
                          step={0.01}
                          roundOnBlur
                        />
                      </td>
                    </tr>
                  );
                }}
              />
            </tbody>
          </>
        )}
      </table>

      {selected?.subfees?.length > 0 && (
        <>
          <h3 style={{ paddingLeft: '35px' }}>{lang.surcharges}</h3>
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th>{lang.name}</th>
                <th>{lang.originalPrice}</th>
                <th>{lang.adjustedPrice}</th>
              </tr>
            </thead>
            <tbody>
              {selected?.subfees.map((subfee, index) => {
                const adjustedAmount = form.data.refundSubfees?.[index]?.amount ?? subfee.amount;

                return (
                  <tr key={subfee.subfeeId}>
                    <td>{subfee.name}</td>

                    <td>
                      <FormatValue value={`${(subfee?.amount ?? 0).toFixed(2)}$`} />
                    </td>
                    <td>
                      <NumberInput
                        name="amount"
                        value={adjustedAmount}
                        onChange={({ amount }) => {
                          const updatedSubfees = [...(form.data.refundSubfees || [])];
                          updatedSubfees[index] = { ...subfee, amount };
                          form.merge({
                            refundSubfees: updatedSubfees,
                          });
                        }}
                        style={{ formInput: { minWidth: '157px' }, input: { textAlign: 'right' } }}
                        icon={<Icon class="fas-dollar-sign" />}
                        min={0}
                        step={0.01}
                        roundOnBlur
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      {form.data.refundEnhancements?.length > 0 && (
        <>
          <h3 style={{ paddingLeft: '35px' }}>{lang.enhancementsDistributions}</h3>

          {form.data.refundEnhancements.map((refundEnhancement, index) => {
            const pricingDistributions = refundEnhancement.pricingDistributions || {};
            const distributorDistributions = refundEnhancement.distributorDistributions || {};

            const hasPricingDistributions = Object.keys(pricingDistributions).length > 0;
            const hasDistributorDistributions = !isDealer && Object.keys(distributorDistributions).length > 0;

            if (!hasPricingDistributions && !hasDistributorDistributions) return null;

            const oldEnh = selected?.contractEnhancements?.find((it) => it.id === refundEnhancement.enhancementId);

            return (
              <div key={`enh-${refundEnhancement.enhancementId || index}`} style={{ marginBottom: '32px' }}>
                <h4 style={{ paddingLeft: '65px', marginBottom: '12px' }}>{refundEnhancement.name}</h4>

                <table className={ThemeTableSimple}>
                  {hasPricingDistributions && (
                    <>
                      <thead>
                        <tr>
                          <th>{lang.pricingDistributions}</th>
                          <th>{lang.originalPrice}</th>
                          <th>{lang.adjustedPrice}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <DataObjectMap
                          data={pricingDistributions}
                          render={({ data: pricingInfo, key }) => (
                            <tr key={key}>
                              <td>{key}</td>
                              <td>
                                <FormatValue
                                  value={`${(oldEnh?.pricingDistributions[key]?.amount ?? 0).toFixed(2)}$`}
                                />
                              </td>
                              <td>
                                <NumberInput
                                  name="amount"
                                  value={pricingInfo.amount}
                                  onChange={({ amount }) => {
                                    const updated = [...form.data.refundEnhancements];
                                    updated[index] = {
                                      ...updated[index],
                                      pricingDistributions: {
                                        ...updated[index].pricingDistributions,
                                        [key]: { amount },
                                      },
                                    };
                                    form.merge({ refundEnhancements: updated });
                                  }}
                                  style={{ formInput: { minWidth: '157px' }, input: { textAlign: 'right' } }}
                                  icon={<Icon class="fas-dollar-sign" />}
                                  min={0}
                                  step={0.01}
                                  roundOnBlur
                                />
                              </td>
                            </tr>
                          )}
                        />
                      </tbody>
                    </>
                  )}

                  {hasDistributorDistributions && (
                    <>
                      <thead>
                        <tr>
                          <th>{lang.distributorDistributions}</th>
                          <th>{lang.originalPrice}</th>
                          <th>{lang.adjustedPrice}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <DataObjectMap
                          data={distributorDistributions}
                          render={({ data: pricingInfo, key }) => (
                            <tr key={key}>
                              <td>{key}</td>
                              <td>
                                <FormatValue
                                  value={`${(oldEnh?.distributorDistributions[key]?.amount ?? 0).toFixed(2)}$`}
                                />
                              </td>
                              <td>
                                <NumberInput
                                  name="amount"
                                  value={pricingInfo.amount}
                                  onChange={({ amount }) => {
                                    const updated = [...form.data.refundEnhancements];
                                    updated[index] = {
                                      ...updated[index],
                                      distributorDistributions: {
                                        ...updated[index].distributorDistributions,
                                        [key]: { amount },
                                      },
                                    };
                                    form.merge({ refundEnhancements: updated });
                                  }}
                                  style={{ formInput: { minWidth: '157px' }, input: { textAlign: 'right' } }}
                                  icon={<Icon class="fas-dollar-sign" />}
                                  min={0}
                                  step={0.01}
                                  roundOnBlur
                                />
                              </td>
                            </tr>
                          )}
                        />
                      </tbody>
                    </>
                  )}
                </table>
              </div>
            );
          })}
        </>
      )}
    </>
  );
};

export default ContractRefundCancellationRefund;
